"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { Activity, AlertTriangle, BarChart3, Building2, Cpu, Eye, FlaskConical, Gauge, GitMerge, Handshake, KeyRound, LayoutDashboard, LogOut, Mail, Menu, MessageCircle, MessageSquare, Percent, PlaneLanding, PlaneTakeoff, Route, Server, Settings, ShieldAlert, ShieldCheck, Sparkles, Wallet, Zap } from "lucide-react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { WhiteLabelSeatWarning } from "@/components/white-label-seat-warning";
import { useUser } from "@/hooks/useUser";
import { isStaffHiddenPage } from "@/lib/admin-role";
import { useAdminRole } from "@/lib/admin-role-context";
import { useAuth } from "@/lib/auth-client";
import { formatPlanTermLabel, getEnterpriseLicenseTerm } from "@llmgateway/shared";
import { Logo } from "./ui/logo";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /**
   * "prefix" highlights the entry on its detail routes too; "exact" keeps the
   * highlight on the index page only.
   */
  match: "exact" | "prefix";
}
const navItems: NavItem[] = [{
  href: "/",
  label: "Dashboard",
  icon: LayoutDashboard,
  match: "exact"
}, {
  href: "/organizations",
  label: "Organizations",
  icon: Building2,
  match: "prefix"
}, {
  href: "/crm",
  label: "Enterprise CRM",
  icon: Handshake,
  match: "prefix"
}, {
  href: "/devpass",
  label: "DevPass",
  icon: Sparkles,
  match: "prefix"
}, {
  href: "/chat-plans",
  label: "Lounge Plans",
  icon: MessageSquare,
  match: "prefix"
}, {
  href: "/sdk",
  label: "LLM SDK",
  icon: Wallet,
  match: "prefix"
}, {
  href: "/global-stats",
  label: "Global Stats",
  icon: BarChart3,
  match: "prefix"
}, {
  href: "/load",
  label: "Gateway Load",
  icon: Zap,
  match: "prefix"
}, {
  href: "/discounts",
  label: "Discounts",
  icon: Percent,
  match: "exact"
}, {
  href: "/rate-limits",
  label: "Global Rate Limits",
  icon: Gauge,
  match: "exact"
}, {
  href: "/providers",
  label: "Providers",
  icon: Server,
  match: "exact"
}, {
  href: "/provider-credentials",
  label: "Provider Credentials",
  icon: KeyRound,
  match: "prefix"
}, {
  href: "/models",
  label: "Models",
  icon: Cpu,
  match: "exact"
}, {
  href: "/model-provider-mappings",
  label: "Model Mappings",
  icon: GitMerge,
  match: "exact"
}, {
  href: "/routing-analytics",
  label: "Routing Analytics",
  icon: Route,
  match: "prefix"
}, {
  href: "/unstable-mappings",
  label: "Unstable Mappings",
  icon: Activity,
  match: "prefix"
}, {
  href: "/benchmarks",
  label: "Benchmarks",
  icon: FlaskConical,
  match: "prefix"
}, {
  href: "/contact-submissions",
  label: "Contact Submissions",
  icon: Mail,
  match: "prefix"
}, {
  href: "/provider-listing-requests",
  label: "Provider Requests",
  icon: Building2,
  match: "prefix"
}, {
  href: "/airside-filings",
  label: "Airside Filings",
  icon: PlaneTakeoff,
  match: "prefix"
}, {
  href: "/airside-carriers",
  label: "Airside Carriers",
  icon: PlaneLanding,
  match: "prefix"
}, {
  href: "/chat-support-logs",
  label: "Chat Support Logs",
  icon: MessageCircle,
  match: "prefix"
}, {
  href: "/payment-failures",
  label: "Payment Failures",
  icon: AlertTriangle,
  match: "prefix"
}, {
  href: "/flagged-accounts",
  label: "Flagged Accounts",
  icon: ShieldAlert,
  match: "prefix"
}, {
  href: "/limit-hits",
  label: "Limit Hits",
  icon: ShieldAlert,
  match: "prefix"
}, {
  href: "/content-filter",
  label: "Content Filter",
  icon: ShieldCheck,
  match: "exact"
}, {
  href: "/settings",
  label: "Settings",
  icon: Settings,
  match: "exact"
}];
function isActive(item: NavItem, pathname: string): boolean {
  if (item.href === "/") {
    return pathname === "/" || pathname === "";
  }
  return item.match === "exact" ? pathname === item.href : pathname.startsWith(item.href);
}
function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "long",
    day: "numeric",
    year: "numeric"
  });
}
interface AdminShellProps {
  children: ReactNode;
  /**
   * Server-rendered session-cookie presence. Used only as the fallback while
   * `/user/me` is still in flight, so the navigation does not flash in or out
   * on every full page load.
   */
  signedIn: boolean;
}
function MobileHeader() {
  const $ = _c(9);
  const {
    toggleSidebar
  } = useSidebar();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Menu className="h-5 w-5" />;
    t1 = <span className="sr-only">Toggle menu</span>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== toggleSidebar) {
    t2 = <Button variant="ghost" size="icon" className="h-9 w-9" onClick={toggleSidebar}>{t0}{t1}</Button>;
    $[2] = toggleSidebar;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"><Logo className="h-4 w-4" /></div>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-2">{t3}<div className="flex flex-col"><span className="text-sm font-semibold leading-tight">LLM Gateway</span><span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Admin</span></div></div>;
    t5 = <ThemeToggle size="compact" className="ml-auto" />;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2) {
    t6 = <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-border/60 bg-background px-4 md:hidden">{t2}{t4}{t5}</header>;
    $[7] = t2;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
export function AdminShell(t0) {
  const $ = _c(63);
  const {
    children,
    signedIn
  } = t0;
  const pathname = usePathname();
  const router = useRouter();
  const {
    signOut
  } = useAuth();
  const queryClient = useQueryClient();
  const {
    user,
    isLoading,
    error,
    sessionRejected,
    data
  } = useUser();
  const adminRole = useAdminRole();
  let T0;
  let T1;
  let license;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== adminRole || $[1] !== children || $[2] !== data?.enterpriseLicense || $[3] !== error || $[4] !== isLoading || $[5] !== pathname || $[6] !== queryClient || $[7] !== router || $[8] !== sessionRejected || $[9] !== signOut || $[10] !== signedIn || $[11] !== user) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const visibleNavItems = adminRole === "admin" ? navItems : navItems.filter(_temp);
      const showNav = user ? !!user.adminRole : isLoading && !error && !sessionRejected && signedIn;
      license = data?.enterpriseLicense;
      const licenseTerm = getEnterpriseLicenseTerm(license?.expiresAt);
      const whiteLabelExpiryTerm = license?.kind === "white_label" && license.status === "active" && licenseTerm !== null && (licenseTerm.status === "expiring" || licenseTerm.status === "critical") ? licenseTerm : null;
      if (!showNav) {
        let t7;
        if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <div className="absolute right-4 top-4 z-50"><ThemeToggle size="compact" /></div>;
          $[21] = t7;
        } else {
          t7 = $[21];
        }
        let t8;
        if ($[22] !== children) {
          t8 = <div className="relative min-h-svh w-full">{t7}{children}</div>;
          $[22] = children;
          $[23] = t8;
        } else {
          t8 = $[23];
        }
        t6 = t8;
        break bb0;
      }
      let t7;
      if ($[24] !== queryClient || $[25] !== router || $[26] !== signOut) {
        t7 = async () => {
          await signOut({
            fetchOptions: {
              onSuccess: () => {
                queryClient.clear();
                router.push("/login");
              }
            }
          });
        };
        $[24] = queryClient;
        $[25] = router;
        $[26] = signOut;
        $[27] = t7;
      } else {
        t7 = $[27];
      }
      const handleSignOut = t7;
      T1 = SidebarProvider;
      let t8;
      if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"><Logo className="h-4 w-4" /></div>;
        $[28] = t8;
      } else {
        t8 = $[28];
      }
      let t9;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <SidebarHeader className="border-b border-sidebar-border/60"><div className="flex h-12 items-center justify-between px-2"><div className="flex items-center gap-2 px-1">{t8}<div className="flex flex-col"><span className="text-sm font-semibold leading-tight">LLM Gateway</span><span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Admin</span></div></div><SidebarTrigger className="hidden md:flex" /></div></SidebarHeader>;
        $[29] = t9;
      } else {
        t9 = $[29];
      }
      let t10;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = <SidebarGroupLabel>Main</SidebarGroupLabel>;
        $[30] = t10;
      } else {
        t10 = $[30];
      }
      let t11;
      if ($[31] !== pathname) {
        t11 = item_0 => {
          const Icon = item_0.icon;
          return <SidebarMenuItem key={item_0.href}><Link href={item_0.href} prefetch={true} className="block"><SidebarMenuButton isActive={isActive(item_0, pathname)} size="lg"><Icon className="h-4 w-4" /><span>{item_0.label}</span></SidebarMenuButton></Link></SidebarMenuItem>;
        };
        $[31] = pathname;
        $[32] = t11;
      } else {
        t11 = $[32];
      }
      const t12 = visibleNavItems.map(t11);
      let t13;
      if ($[33] !== t12) {
        t13 = <SidebarContent><SidebarGroup>{t10}<SidebarMenu>{t12}</SidebarMenu></SidebarGroup></SidebarContent>;
        $[33] = t12;
        $[34] = t13;
      } else {
        t13 = $[34];
      }
      let t14;
      if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = <div className="flex justify-center"><ThemeToggle size="compact" /></div>;
        $[35] = t14;
      } else {
        t14 = $[35];
      }
      let t15;
      let t16;
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = <LogOut className="h-3.5 w-3.5" />;
        t16 = <span>Sign out</span>;
        $[36] = t15;
        $[37] = t16;
      } else {
        t15 = $[36];
        t16 = $[37];
      }
      let t17;
      if ($[38] !== handleSignOut) {
        t17 = <SidebarFooter className="border-t border-sidebar-border/60">{t14}<Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-xs text-muted-foreground" onClick={handleSignOut}>{t15}{t16}</Button></SidebarFooter>;
        $[38] = handleSignOut;
        $[39] = t17;
      } else {
        t17 = $[39];
      }
      if ($[40] !== t13 || $[41] !== t17) {
        t5 = <Sidebar variant="inset">{t9}{t13}{t17}</Sidebar>;
        $[40] = t13;
        $[41] = t17;
        $[42] = t5;
      } else {
        t5 = $[42];
      }
      T0 = SidebarInset;
      if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <MobileHeader />;
        $[43] = t1;
      } else {
        t1 = $[43];
      }
      if ($[44] !== adminRole) {
        t2 = adminRole && adminRole !== "admin" && <Alert className="rounded-none border-x-0 border-t-0 border-sky-500/40 bg-sky-500/10 px-6 text-sky-950 dark:text-sky-100"><Eye /><AlertTitle>{adminRole === "support" ? "Support access" : "Read-only access"}</AlertTitle><AlertDescription className="text-current/90">{adminRole === "support" ? "You can view everything and issue refunds. Other changes need an administrator." : "You can view everything. Changes need an administrator."}</AlertDescription></Alert>;
        $[44] = adminRole;
        $[45] = t2;
      } else {
        t2 = $[45];
      }
      if ($[46] !== license?.kind || $[47] !== user?.isAdmin) {
        t3 = user?.isAdmin && license?.kind === "white_label" && <WhiteLabelSeatWarning />;
        $[46] = license?.kind;
        $[47] = user?.isAdmin;
        $[48] = t3;
      } else {
        t3 = $[48];
      }
      t4 = whiteLabelExpiryTerm && <Alert className={whiteLabelExpiryTerm.status === "critical" ? "rounded-none border-x-0 border-t-0 border-red-500/40 bg-red-500/10 px-6 text-red-950 dark:text-red-100" : "rounded-none border-x-0 border-t-0 border-orange-500/40 bg-orange-500/10 px-6 text-orange-950 dark:text-orange-100"}><AlertTriangle /><AlertTitle>White-label license expires soon</AlertTitle><AlertDescription className="text-current/90">Install a renewed license before{" "}{formatDate(whiteLabelExpiryTerm.expiresAt)} ({formatPlanTermLabel(whiteLabelExpiryTerm).toLowerCase()}) to keep admin access.</AlertDescription></Alert>;
    }
    $[0] = adminRole;
    $[1] = children;
    $[2] = data?.enterpriseLicense;
    $[3] = error;
    $[4] = isLoading;
    $[5] = pathname;
    $[6] = queryClient;
    $[7] = router;
    $[8] = sessionRejected;
    $[9] = signOut;
    $[10] = signedIn;
    $[11] = user;
    $[12] = T0;
    $[13] = T1;
    $[14] = license;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    T0 = $[12];
    T1 = $[13];
    license = $[14];
    t1 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[49] !== license) {
    t7 = license?.kind === "white_label" && license.status === "grace" && <Alert className="rounded-none border-x-0 border-t-0 border-red-500/40 bg-red-500/10 px-6 text-red-950 dark:text-red-100"><AlertTriangle /><AlertTitle>White-label license expired</AlertTitle><AlertDescription className="text-current/90">Admin access remains available during the seven-day grace period. Install a renewed license{license.graceEndsAt ? ` before ${formatDate(new Date(license.graceEndsAt))}` : " before grace ends"}.</AlertDescription></Alert>;
    $[49] = license;
    $[50] = t7;
  } else {
    t7 = $[50];
  }
  let t8;
  if ($[51] !== T0 || $[52] !== children || $[53] !== t1 || $[54] !== t2 || $[55] !== t3 || $[56] !== t4 || $[57] !== t7) {
    t8 = <T0>{t1}{t2}{t3}{t4}{t7}{children}</T0>;
    $[51] = T0;
    $[52] = children;
    $[53] = t1;
    $[54] = t2;
    $[55] = t3;
    $[56] = t4;
    $[57] = t7;
    $[58] = t8;
  } else {
    t8 = $[58];
  }
  let t9;
  if ($[59] !== T1 || $[60] !== t5 || $[61] !== t8) {
    t9 = <T1>{t5}{t8}</T1>;
    $[59] = T1;
    $[60] = t5;
    $[61] = t8;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  return t9;
}
function _temp(item) {
  return !isStaffHiddenPage(item.href);
}